import { useEffect, useState } from 'react';
import { safeStorage } from '../utils/storage';

const COLLAPSED_KEY = 'ultrawideAsideCollapsed';

/**
 * Human-readable relative time. "Hace 5min" / "Hace 2h" / "Ayer" / "Hace 3d".
 * Returns a localized absolute date for anything older than a week.
 */
function timeAgo(iso) {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  const diff = Date.now() - then;
  if (diff < 0) return 'Ahora';
  const m = Math.floor(diff / 60000);
  if (m < 1)    return 'Ahora';
  if (m < 60)   return `Hace ${m}min`;
  const h = Math.floor(m / 60);
  if (h < 24)   return `Hace ${h}h`;
  const d = Math.floor(h / 24);
  if (d === 1)  return 'Ayer';
  if (d < 7)    return `Hace ${d}d`;
  return new Date(iso).toLocaleDateString();
}

/**
 * Right-rail panel shown only on ultrawide screens (>= 1700px).
 * Surfaces actionable info that's normally hidden behind a sidebar click:
 *   - Progress recap with a big % and the "to go" count
 *   - Pending incoming proposals
 *   - Top 5 missing rare/legendary stickers ("joyas faltantes")
 *   - Last 4 history entries (trades), with relative timestamps
 *
 * Collapsible: a small chevron on the left edge collapses the panel
 * to a slim handle. State persists across reloads.
 */
export default function UltrawideAside({
  totalProgress,
  totalDuplicates,
  totalReserved = 0,
  missingLegendaries,
  incomingProposals,
  history,
  onNavigateToProposals,
  onJumpToSticker,
}) {
  const [shouldShow, setShouldShow] = useState(false);
  const [collapsed, setCollapsed] = useState(() => safeStorage.get(COLLAPSED_KEY) === '1');

  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1700px)');
    const apply = () => setShouldShow(mq.matches);
    apply();
    mq.addEventListener('change', apply);
    return () => mq.removeEventListener('change', apply);
  }, []);

  const toggleCollapsed = () => {
    setCollapsed(prev => {
      const next = !prev;
      safeStorage.set(COLLAPSED_KEY, next ? '1' : '0');
      return next;
    });
  };

  if (!shouldShow) return null;

  // Collapsed: render only a slim handle with a chevron
  if (collapsed) {
    return (
      <aside className="ultrawide-aside ultrawide-aside-collapsed" aria-label="Panel lateral (colapsado)">
        <button
          className="aside-toggle"
          onClick={toggleCollapsed}
          aria-label="Expandir panel lateral"
          title="Expandir panel"
        >‹</button>
      </aside>
    );
  }

  const missing = totalProgress.total - totalProgress.have;
  const availableDuplicates = Math.max(0, totalDuplicates - totalReserved);
  const recentHistory = history?.slice(0, 4) || [];

  return (
    <aside className="ultrawide-aside" aria-label="Panel lateral de progreso">
      {/* Collapse toggle */}
      <button
        className="aside-toggle aside-toggle-inline"
        onClick={toggleCollapsed}
        aria-label="Colapsar panel lateral"
        title="Colapsar panel"
      >›</button>

      {/* Progress recap */}
      <section className="aside-card">
        <div className="aside-card-label">Tu progreso</div>
        <div className="aside-progress-pct">{totalProgress.pct}%</div>
        <div className="aside-progress-row">
          <div>
            <div className="aside-stat-num">{totalProgress.have}</div>
            <div className="aside-stat-lbl">tengo</div>
          </div>
          <div>
            <div className="aside-stat-num" style={{ color: '#60a5fa' }}>{missing}</div>
            <div className="aside-stat-lbl">me faltan</div>
          </div>
          <div title={totalReserved > 0
            ? `${availableDuplicates} disponibles para intercambiar (${totalDuplicates} totales − ${totalReserved} reservadas)`
            : `${totalDuplicates} láminas repetidas`}>
            <div className="aside-stat-num" style={{ color: '#4ade80' }}>{availableDuplicates}</div>
            <div className="aside-stat-lbl">{totalReserved > 0 ? 'disponibles' : 'repetidas'}</div>
          </div>
        </div>
        {totalReserved > 0 && (
          <div className="aside-reserved-row">
            <span>🔒</span>
            <strong>{totalReserved}</strong>
            <span>reservadas para canjes</span>
          </div>
        )}
      </section>

      {/* Pending proposals */}
      {incomingProposals?.length > 0 && (
        <section className="aside-card aside-card-clickable" onClick={onNavigateToProposals}>
          <div className="aside-card-label">
            <span style={{ color: '#f87171' }}>● </span>
            Propuestas pendientes
          </div>
          <div className="aside-proposals-count">{incomingProposals.length}</div>
          <div className="aside-proposals-names">
            {incomingProposals.slice(0, 3).map(p => (
              <span key={p.id}>{p.fromName || 'Un coleccionista'}</span>
            ))}
            {incomingProposals.length > 3 && <span>+{incomingProposals.length - 3} más</span>}
          </div>
          <div className="aside-cta">Ver todas →</div>
        </section>
      )}

      {/* Top missing rare/legendaries */}
      {missingLegendaries?.length > 0 && (
        <section className="aside-card">
          <div className="aside-card-label">💎 Joyas faltantes</div>
          <div className="aside-gems-list">
            {missingLegendaries.slice(0, 5).map(s => (
              <button
                key={s.number}
                className="aside-gem"
                onClick={() => onJumpToSticker?.(s)}
                aria-label={`Ir a ${s.number} ${s.label}`}
              >
                <span className="aside-gem-num">{s.number}</span>
                <span className="aside-gem-name">{s.label}</span>
              </button>
            ))}
          </div>
        </section>
      )}

      {/* Recent activity — last few history entries */}
      {recentHistory.length > 0 && (
        <section className="aside-card">
          <div className="aside-card-label">🕒 Actividad reciente</div>
          <ul className="aside-activity-list">
            {recentHistory.map(item => (
              <li key={item.id} className="aside-activity-item">
                <div className="aside-activity-icon">
                  {item.type === 'trade' ? '🤝' : '✨'}
                </div>
                <div className="aside-activity-body">
                  <div className="aside-activity-title">
                    {item.type === 'trade'
                      ? `Canje con ${item.friendName || 'amigo'}`
                      : 'Actividad'}
                  </div>
                  {item.type === 'trade' && (
                    <div className="aside-activity-sub">
                      +{item.receive} · −{item.give}
                    </div>
                  )}
                </div>
                <div className="aside-activity-time">{timeAgo(item.date)}</div>
              </li>
            ))}
          </ul>
        </section>
      )}
    </aside>
  );
}
